import {RuleEventType, Spot, TriggerRule} from "../../../components/types";

export type SpotRuleGraphEdge = {
    id: string;
    fromId: string;
    toId: string;
    rule: TriggerRule;
};

export type SpotRuleGraph = {
    spotByNodeId: Map<string, Spot>;
    edges: SpotRuleGraphEdge[];
    incomingByNodeId: Map<string, SpotRuleGraphEdge[]>;
    outgoingByNodeId: Map<string, SpotRuleGraphEdge[]>;
    weakComponents: string[][];
    startNodeIds: string[];
};

const POSITIVE_EVENTS = new Set<RuleEventType>([
    "SPOT_TRIGGERED",
    "SPOT_FINISHED",
    "SPOT_FINISHED_FOR_MS",
    "AMBIENT_TRIGGERED",
    "AMBIENT_TIME_SINCE_TRIGGERED"
]);

const pushEdge = (map: Map<string, SpotRuleGraphEdge[]>, key: string, edge: SpotRuleGraphEdge) => {
    const list = map.get(key);
    if (list) {
        list.push(edge);
        return;
    }
    map.set(key, [edge]);
};

export const isPositiveSpotEvent = (eventType?: TriggerRule["eventType"]) => {
    if (!eventType) {
        return false;
    }
    return POSITIVE_EVENTS.has(eventType as RuleEventType);
};

export const getSpotNodeId = (spot: Spot) => spot.key || spot.id || spot.name || "";

/**
 * Shared directed graph model for spot-to-spot positive dependencies.
 * This is intentionally reusable by both flow visualization and cue grouping.
 */
export const buildSpotRuleGraph = (spots: Spot[]): SpotRuleGraph => {
    const spotByNodeId = new Map<string, Spot>();
    spots.forEach((spot) => {
        const nodeId = getSpotNodeId(spot);
        if (!nodeId) {
            return;
        }
        if (!spotByNodeId.has(nodeId)) {
            spotByNodeId.set(nodeId, {...spot, key: nodeId});
        }
    });

    const edges: SpotRuleGraphEdge[] = [];
    spotByNodeId.forEach((spot) => {
        const rules = spot.triggerRules?.rules || [];
        rules.forEach((rule) => {
            if (rule.type !== "Spot" || !rule.spotKey || !isPositiveSpotEvent(rule.eventType)) {
                return;
            }
            if (!spotByNodeId.has(rule.spotKey)) {
                return;
            }
            edges.push({
                id: `edge-${rule.spotKey}-${spot.key}-${rule.id || rule.eventType}`,
                fromId: rule.spotKey,
                toId: spot.key,
                rule
            });
        });
    });

    const incomingByNodeId = new Map<string, SpotRuleGraphEdge[]>();
    const outgoingByNodeId = new Map<string, SpotRuleGraphEdge[]>();
    Array.from(spotByNodeId.keys()).forEach((nodeId) => {
        incomingByNodeId.set(nodeId, []);
        outgoingByNodeId.set(nodeId, []);
    });
    edges.forEach((edge) => {
        pushEdge(incomingByNodeId, edge.toId, edge);
        pushEdge(outgoingByNodeId, edge.fromId, edge);
    });

    const startNodeIds = Array.from(spotByNodeId.keys()).filter((nodeId) => {
        return (incomingByNodeId.get(nodeId) || []).length === 0;
    });

    // Weak components are calculated on the undirected view of directed edges.
    const nodeOrder = Array.from(spotByNodeId.keys());
    const nodeIndex = new Map<string, number>();
    nodeOrder.forEach((nodeId, index) => nodeIndex.set(nodeId, index));
    const visited = new Set<string>();
    const weakComponents: string[][] = [];

    const compareNodeIds = (a: string, b: string) => {
        return (nodeIndex.get(a) ?? Number.MAX_SAFE_INTEGER) - (nodeIndex.get(b) ?? Number.MAX_SAFE_INTEGER);
    };

    nodeOrder.forEach((rootNodeId) => {
        if (visited.has(rootNodeId)) {
            return;
        }
        const queue: string[] = [rootNodeId];
        const component: string[] = [];
        visited.add(rootNodeId);

        while (queue.length > 0) {
            const nodeId = queue.shift();
            if (!nodeId) {
                continue;
            }
            component.push(nodeId);
            const neighbors = [
                ...(outgoingByNodeId.get(nodeId) || []).map((edge) => edge.toId),
                ...(incomingByNodeId.get(nodeId) || []).map((edge) => edge.fromId)
            ].sort(compareNodeIds);
            neighbors.forEach((neighborId) => {
                if (visited.has(neighborId)) {
                    return;
                }
                visited.add(neighborId);
                queue.push(neighborId);
            });
        }

        component.sort(compareNodeIds);
        weakComponents.push(component);
    });

    return {
        spotByNodeId,
        edges,
        incomingByNodeId,
        outgoingByNodeId,
        weakComponents,
        startNodeIds
    };
};
